import React, { useState, useEffect, useCallback, useRef } from 'react';

import CarCard from '../../features/CarCard';
import { IconFlag } from '../../ui/Icons';
import { useVehicles } from '../../../context/VehiclesContext';
import './Inventory.css';

const ITEMS_PER_PAGE = 6;

const InventoryPage = ({ onViewDetails }) => {
  const { vehicles: allVehicles } = useVehicles();

  useEffect(() => {
    document.title = 'All Cars for Sale | JAC Cars';
  }, []);

  const [displayedVehicles, setDisplayedVehicles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const loaderRef = useRef(null);

  const loadMoreVehicles = useCallback(() => {
    if (loading || !hasMore) return;

    setLoading(true);

    setTimeout(() => {
      const currentLength = displayedVehicles.length;
      const nextVehicles = allVehicles.slice(currentLength, currentLength + ITEMS_PER_PAGE);

      if (nextVehicles.length === 0) {
        setHasMore(false);
      } else {
        setDisplayedVehicles(prev => [...prev, ...nextVehicles]);
        if (currentLength + nextVehicles.length >= allVehicles.length) {
          setHasMore(false);
        }
      }
      setLoading(false);
    }, 500);
  }, [allVehicles, displayedVehicles.length, loading, hasMore]);

  // Vehicles arrive asynchronously (fetched from the API), so reset to the
  // first page whenever the fetched list changes rather than only on mount.
  useEffect(() => {
    setDisplayedVehicles(allVehicles.slice(0, ITEMS_PER_PAGE));
    setHasMore(allVehicles.length > ITEMS_PER_PAGE);
  }, [allVehicles]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loading) {
          loadMoreVehicles();
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [loadMoreVehicles, hasMore, loading]);

  return (
    <div className="inventory-page">
      <div className="inventory-hero">
        <div className="container">
          <h1>Our Inventory</h1>
          <p>Explore our complete collection of premium vehicles</p>
        </div>
      </div>

      <div className="container">
        <div className="inventory-grid">
          {displayedVehicles.map(car => (
            <CarCard 
              key={car.id} 
              {...car} 
              onViewDetails={() => onViewDetails && onViewDetails(car)} 
            />
          ))}
        </div>

        <div ref={loaderRef} className="inventory-loader">
          {loading && (
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading more vehicles...</p>
            </div>
          )}
          
          {!hasMore && displayedVehicles.length > 0 && (
            <div className="end-of-stock">
              <div className="end-icon"><IconFlag /></div>
              <h3>You've Reached the End</h3>
              <p>That's our complete inventory! Can't find what you're looking for?</p>
              <button className="btn btn-primary">Contact Us for Special Requests</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InventoryPage;
